import { useTranslation } from 'react-i18next';
import { ContentLanguageCode, AUDIO_UPLOAD } from '@/shared/constants';
import speakerIcon from '@/assets/svg/speaker_icon.svg';
import { AudioPlayer } from './AudioPlayer';
import { TranscriptionEditor } from './TranscriptionEditor';

interface WorkAudioPanelProps {
  currentLanguage: ContentLanguageCode;
  transcription: Record<ContentLanguageCode, string>;
  setTranscription: (transcription: Record<ContentLanguageCode, string>) => void;
  audioPreview: Record<ContentLanguageCode, string | null>;
  audioInputRef: React.RefObject<HTMLInputElement>;
  audioUploadError: string | null;
  exhibitionColor?: string;
  markDirty: () => void;
  onAudioClick: () => void;
  onAudioSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAudio: () => void;
}

/** Onglet « Commentaire audio » (Figma admin 324:5985) : audio de l'épisode et sa transcription. */
export const WorkAudioPanel: React.FC<WorkAudioPanelProps> = ({
  currentLanguage,
  transcription,
  setTranscription,
  audioPreview,
  audioInputRef,
  audioUploadError,
  exhibitionColor,
  markDirty,
  onAudioClick,
  onAudioSelect,
  onRemoveAudio,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="create-work-section">
        <h4 className="section-label">{t('exhibitions.audioDescription.audioLabel')}</h4>
        <input
          ref={audioInputRef}
          type="file"
          accept={AUDIO_UPLOAD.ACCEPT}
          onChange={onAudioSelect}
          style={{ display: 'none' }}
          aria-label={t('exhibitions.audioDescription.audioLabel')}
        />
        {audioPreview[currentLanguage] ? (
          <AudioPlayer
            audioUrl={audioPreview[currentLanguage]!}
            onRemove={onRemoveAudio}
            exhibitionColor={exhibitionColor}
          />
        ) : (
          <>
            <button type="button" className="upload-box audio-upload" onClick={onAudioClick}>
              <img src={speakerIcon} alt="" className="audio-icon" />
              <span className="upload-audio-text">
                <span>{t('exhibitions.audioUploadPrompt')}</span>
                <span className="upload-audio-hint">
                  {t('exhibitions.audioUploadFormatsMax', { maxSize: AUDIO_UPLOAD.MAX_SIZE_MB })}
                </span>
              </span>
            </button>
            {audioUploadError && (
              <p className="upload-audio-error" role="alert">
                {audioUploadError}
              </p>
            )}
          </>
        )}
      </div>

      <div className="create-work-section">
        <h4 className="section-label section-label--with-hint">
          {t('exhibitions.audioDescription.transcriptionLabel')}
        </h4>
        <p className="create-work-hint">{t('works.audio.transcriptionHint')}</p>
        <TranscriptionEditor
          value={transcription[currentLanguage] || ''}
          onChange={(value) => {
            markDirty();
            setTranscription({ ...transcription, [currentLanguage]: value });
          }}
          exhibitionColor={exhibitionColor}
        />
      </div>
    </>
  );
};
